/**
 * Individual OKR Node Component
 * Custom ReactFlow node for Individual/Personal OKRs
 */

import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { OKR } from '../../../types/okr';
import { keyResultProgressPercent } from '../../../utils/progress';

interface IndividualOKRNodeData {
  okr: OKR;
  selected?: boolean;
}

export const IndividualOKRNode: React.FC<NodeProps<IndividualOKRNodeData>> = ({ data }) => {
  const { okr, selected = false } = data;
  const progress = okr.progress || 0;
  const keyResults = okr.keyResults || [];
  const displayKRs = keyResults.slice(0, 3); // Show max 3 KRs
  const remainingKRs = keyResults.length - displayKRs.length;

  // Truncate title if too long
  const truncateTitle = (title: string, maxLength: number = 30) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  // Get initials for avatar
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const initials = getInitials(okr.owner.firstName, okr.owner.lastName);
  const ownerName = `${okr.owner.firstName} ${okr.owner.lastName}`;

  return (
    <div className={`individual-okr-node ${selected ? 'ring-4 ring-yellow-400 ring-offset-2 rounded-lg' : ''}`}>
      {/* Top handle for incoming connections */}
      <Handle type="target" position={Position.Top} className="!bg-purple-600" />

      {/* Node content */}
      <div className={`w-[200px] bg-gradient-to-br from-purple-500 to-purple-600 border-2 ${selected ? 'border-yellow-400' : 'border-purple-700'} rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer`}>
        {/* Header */}
        <div className="px-3 py-2.5 border-b border-purple-400/30">
          <div className="flex items-center gap-2 mb-1.5">
            {/* Avatar */}
            {okr.owner.profilePicture ? (
              <img
                src={okr.owner.profilePicture}
                alt={ownerName}
                className="w-6 h-6 rounded-full border border-white/30"
              />
            ) : (
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center border border-white/30">
                <span className="text-xs font-bold text-white">{initials}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-purple-100 truncate">
                {ownerName}
              </div>
            </div>
          </div>
          <h3 className="text-white font-semibold text-sm leading-tight">
            {truncateTitle(okr.title)}
          </h3>
        </div>

        {/* Progress */}
        <div className="px-3 py-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-purple-100">Progress</span>
            <span className="text-xs font-bold text-white">{progress}%</span>
          </div>
          <div className="w-full bg-purple-800/40 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-white to-purple-100 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Key Results */}
        {displayKRs.length > 0 && (
          <div className="px-3 py-2 border-t border-purple-400/20">
            <p className="text-xs font-medium text-purple-100 mb-1.5">Key Results:</p>
            <div className="space-y-1.5">
              {displayKRs.map((kr) => {
                const krProgress = keyResultProgressPercent(kr);
                return (
                  <div key={kr.id} className="text-xs">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-purple-50 truncate pr-2">{truncateTitle(kr.title, 20)}</span>
                      <span className="text-white font-medium text-[10px]">{krProgress}%</span>
                    </div>
                    <div className="w-full bg-purple-800/40 rounded-full h-1 overflow-hidden">
                      <div
                        className="h-full bg-purple-200 rounded-full transition-all duration-300"
                        style={{ width: `${krProgress}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {remainingKRs > 0 && (
                <p className="text-[10px] text-purple-200 italic text-center mt-1">
                  +{remainingKRs} more
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom handle for outgoing connections (rarely used for individual OKRs) */}
      <Handle type="source" position={Position.Bottom} className="!bg-purple-600" />
    </div>
  );
};
